import React, { useState, useEffect, useRef, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  User,
  LogOut,
  Settings,
  BarChart3,
  ChevronRight,
  ChevronLeft,
  Rss,
  LayoutGrid,
  FileText,
  Trophy,
  Lock,
  HelpCircle,
  Headphones,
  Shield
} from 'lucide-react';
import { api } from '../services/api';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { isValidImageUrl } from '../utils/avatarUtils';
import { isCreator } from '../services/auth';

// Admin email whitelist - only these emails have admin access
const ADMIN_EMAILS = ['kazcareers@gmail.com', 'admin@pickscity.com'];

const SideNavbar: React.FC = memo(() => {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [user, setUser] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  // Ref to track pending API requests for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref to track debounce timeout
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Function to check and update user state
  const checkUserState = async () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      // Try to get the full user data with profile picture from the database
      try {
        const dbUser = await api.getUserByEmail(parsedUser.email);

        // Only update state if the request wasn't aborted
        if (!abortControllerRef.current.signal.aborted) {
          if (dbUser && dbUser.profile_picture) {
            // Update the stored user data with the profile picture
            const updatedUser = { ...parsedUser, profile_picture: dbUser.profile_picture, name: dbUser.name || parsedUser.name };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);
          } else {
            setUser(parsedUser);
          }
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name !== 'AbortError') {
          console.error('Error fetching user profile:', error);
          setUser(parsedUser);
        }
      }
    } else {
      setUser(null);
    }
  };

  // Debounced version of checkUserState to prevent rapid consecutive calls
  const debouncedCheckUserState = () => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      checkUserState();
    }, 150); // 150ms debounce
  };

  // Check user state on mount
  useEffect(() => {
    checkUserState();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Listen for storage changes (when user logs in/out in another tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        debouncedCheckUserState();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for custom events (when user logs in/out in same tab)
  useEffect(() => {
    const handleUserChange = () => {
      debouncedCheckUserState();
    };

    window.addEventListener('userStateChanged', handleUserChange);
    return () => window.removeEventListener('userStateChanged', handleUserChange);
  }, []);



  const navItems = [
    { name: 'Shop Picks', path: '/shop-picks', icon: TrendingUp },
    { name: 'Locker Room', path: '/locker-room', icon: Trophy },
    ...(user
      ? [
          { name: 'My Picks Feed', path: '/my-picks', icon: Rss },
          { name: isCreator() ? 'Creator Dashboard' : 'Become a Creator', path: '/dashboard', icon: BarChart3 },
          // Only show Admin Dashboard for admin users
          ...(ADMIN_EMAILS.includes(user.email || '') ? [{ name: 'Admin Dashboard', path: '/admin/login', icon: Shield }] : [])
        ]
      : [
          { name: 'My Picks Feed', path: '/auth', icon: Rss, locked: true },
          { name: 'Become a Creator', path: '/auth', icon: BarChart3, locked: true }
        ]),
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      // signOut() in AuthContext handles navigation, but we'll update local state
      setUser(null);
    } catch (error: any) {
      // Silently handle session-related errors (they mean user is already logged out)
      if (!error?.message?.includes('session') && !error?.message?.includes('Auth session missing')) {
        console.error('Logout error:', error);
      }

      // Always fallback to manual cleanup to ensure logout completes
      localStorage.removeItem('user');
      setUser(null);
      window.dispatchEvent(new Event('userStateChanged'));

      // Force navigation to auth page
      navigate('/auth', { replace: true });
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <motion.nav
      className={`fixed left-0 top-0 h-full z-50 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      } bg-dark-900/95 backdrop-blur-md border-r border-neon-green/20`}
      initial={{ x: -100 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 w-6 h-6 bg-neon-green rounded-full flex items-center justify-center text-dark-900 hover:bg-neon-blue transition-colors duration-200 z-10"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </motion.button>

      <div className="flex flex-col h-full">
        {/* Logo Section */}
        <motion.div 
          className="p-4 border-b border-neon-green/20"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <Link to="/" className="flex items-center space-x-3">
            <motion.div 
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-neon-green to-neon-blue flex items-center justify-center overflow-hidden"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="/images/picksCity.png" 
                alt="PicksCity Logo" 
                className="w-full h-full object-contain"
              />
            </motion.div>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.span 
                  className="font-futuristic font-bold text-xl text-white"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  PicksCity
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </motion.div>

        {/* Profile Section */}
        <div className="p-4 border-b border-neon-green/20">
          {user ? (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-neon-green to-neon-blue flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden">
                {user.profile_picture && isValidImageUrl(user.profile_picture) ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name || 'User'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // If image fails to load, fallback to initials
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const initialsDiv = document.createElement('div');
                      initialsDiv.textContent = getInitials(user.name || user.email || 'U');
                      initialsDiv.className = 'w-full h-full flex items-center justify-center';
                      target.parentElement?.appendChild(initialsDiv);
                    }}
                  />
                ) : user.profile_picture ? (
                  <span className="text-xl">{user.profile_picture}</span>
                ) : (
                  getInitials(user.name || user.email || 'U')
                )}
              </div>
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    className="flex-1 min-w-0"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-white font-medium text-sm truncate">
                      {user.name || 'User'}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-dark-700 flex items-center justify-center text-gray-400 flex-shrink-0">
                <User className="w-5 h-5" />
              </div>
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-gray-400 text-sm">
                      Not signed in
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Navigation Items */}
        <div className="flex-1 py-4">
          <div className="space-y-2 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isLocked = item.locked || false;

              return (
                <motion.div
                  key={item.name}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative"
                >
                  <Link
                    to={item.path}
                    className={`relative flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-sharp font-medium transition-all duration-300 ${
                      isLocked
                        ? 'text-gray-400 hover:bg-dark-800/20'
                        : location.pathname === item.path
                        ? 'bg-gradient-to-r from-dark-800 to-dark-900 text-white'
                        : 'text-white hover:bg-dark-800/30'
                    }`}
                  >
                    {/* Active indicator bar */}
                    {!isLocked && location.pathname === item.path && (
                      <motion.div
                        className="absolute left-0 top-0 bottom-0 w-1 bg-neon-green rounded-r-full"
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2 }}
                      />
                    )}
                    <div className="relative">
                      <Icon className={`w-5 h-5 flex-shrink-0 transition-colors ${
                        isLocked
                          ? 'text-gray-500'
                          : location.pathname === item.path
                          ? 'text-neon-green'
                          : 'text-white'
                      }`} />
                      {isLocked && (
                        <Lock className="w-3 h-3 absolute -top-1 -right-1 text-gray-400" />
                      )}
                    </div>
                    <AnimatePresence>
                      {!isCollapsed && (
                        <motion.div
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex items-center gap-2 flex-1"
                        >
                          <span className={location.pathname === item.path && !isLocked ? 'font-bold' : ''}>
                            {item.name}
                          </span>
                          {isLocked && (
                            <span className="text-xs text-gray-500">(Sign in required)</span>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Legal Links Section */}
        <div className="p-3 border-t border-neon-green/20">
          <div className="space-y-1">
            <Link
              to="/privacy-policy"
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-neon-green hover:bg-neon-green/5 transition-all duration-200"
            >
              <FileText className="w-3 h-3 flex-shrink-0" />
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Privacy Policy
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
            <Link
              to="/terms-of-service"
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-neon-green hover:bg-neon-green/5 transition-all duration-200"
            >
              <FileText className="w-3 h-3 flex-shrink-0" />
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Terms of Service
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
            <Link
              to="/support"
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-white hover:text-neon-green hover:bg-neon-green/5 transition-all duration-200"
            >
              <Headphones className="w-4 h-4 flex-shrink-0" />
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Support
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="p-3 border-t border-neon-green/20">
          {user ? (
            <div className="space-y-2">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 w-full text-left"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      Logout
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </div>
          ) : (
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/auth"
                className="btn-glow flex items-center justify-center px-3 py-3 text-sm"
              >
                <User className="w-5 h-5 flex-shrink-0" />
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-2"
                    >
                      Sign In
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </motion.nav>
  );
});

SideNavbar.displayName = 'SideNavbar';

export default SideNavbar; 